import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Box,
} from '@mui/material';
import { versionService } from '@/services/versionService';
import { errorReporter } from '@/services/errorReportingService';

interface VersionCheckerProps {
  children: React.ReactNode;
}

const VersionChecker = ({ children }: VersionCheckerProps) => {
  const [isChecking, setIsChecking] = useState(true);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        // Use enhanced version check that includes remote comparison
        const versionChanged = await versionService.enhancedVersionCheck();

        if (versionChanged) {
          setShowUpdateDialog(true);
          setIsChecking(false); // Stop showing the loading spinner
        } else {
          setIsChecking(false);
        }
      } catch (error) {
        errorReporter.expected('Version check failed', error, { feature: 'app', operation: 'versionCheck' });
        // Continue with app loading even if version check fails
        setIsChecking(false);
      }
    };

    checkVersion();
  }, []);

  const handleUpdate = async () => {
    setIsUpdating(true);
    // Small delay to show loading state, then await the refresh
    await new Promise((resolve) => setTimeout(resolve, 500));
    await versionService.forceRefresh();
  };

  const handleSkip = () => {
    setShowUpdateDialog(false);
    setIsChecking(false);
  };

  // Show loading while checking version
  if (isChecking) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Box sx={{ color: 'text.secondary', fontSize: '14px' }}>Checking for updates...</Box>
      </Box>
    );
  }

  return (
    <>
      {children}

      {/* Update Dialog */}
      <Dialog
        open={showUpdateDialog}
        disableEscapeKeyDown
        aria-labelledby="version-update-dialog-title"
        aria-describedby="version-update-dialog-description"
      >
        <DialogTitle id="version-update-dialog-title">App Update Available</DialogTitle>
        <DialogContent>
          <DialogContentText id="version-update-dialog-description">
            A new version of the application is available. To ensure everything works correctly, we recommend refreshing
            the page to load the latest version. Your session data will be cleared to prevent any conflicts.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSkip} color="secondary" disabled={isUpdating}>
            Continue with Current Version
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={16} /> : null}
          >
            {isUpdating ? 'Updating...' : 'Update Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VersionChecker;
